import React from 'react';
import AdminTenantSelector, { type AdminTenantOption } from '../components/AdminTenantSelector';
import { usePortalContext } from '../context/PortalContext';
import { hasElevatedAccess, PORTAL_ROLES } from '../utils/roles';

type TelemetryResponse = {
  window: { fromUtc: string; toUtc: string; days: number };
  eventsByType: Array<{ eventType: string; count: number }>;
  verificationsByResult: Array<{ result: 'match' | 'mismatch' | 'error'; count: number; avgDurationMs?: number }>;
  recentVerifications: Array<{
    verificationId: string;
    evidenceId: string;
    requestedAtUtc: string;
    requestedBy: string;
    result: 'match' | 'mismatch' | 'error';
    reportJsonRef?: string;
    reportPdfRef?: string;
  }>;
};

type SyntheticStatus = {
  enabled: boolean;
  intervalMinutes: number;
  tenants: string[];
  recentRuns: Array<{
    runId: string;
    tenantId: string;
    startedAtUtc: string;
    completedAtUtc: string;
    totalDurationMs: number;
    telemetryQueryMs: number;
    status: 'ok' | 'warning' | 'critical' | 'failed';
    error?: string;
  }>;
};

const EvidenceAdmin = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const canManageEvidence = hasElevatedAccess(tenant?.roles, PORTAL_ROLES.evidenceManager) ||
    hasElevatedAccess(tenant?.roles, PORTAL_ROLES.documentManager);

  const [deployableTenants, setDeployableTenants] = React.useState<AdminTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = React.useState('');
  const [days, setDays] = React.useState(30);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [telemetry, setTelemetry] = React.useState<TelemetryResponse | null>(null);
  const [synthetic, setSynthetic] = React.useState<SyntheticStatus | null>(null);
  const [auditId, setAuditId] = React.useState('');
  const [exportResult, setExportResult] = React.useState<{
    manifestSha256: string;
    evidenceCount: number;
    verificationCount: number;
  } | null>(null);

  const loadTenants = React.useCallback(async (): Promise<void> => {
    if (!canManageEvidence) return;
    try {
      const response = await fetch('/api/admin/spfx-releases/deployable-tenants');
      if (!response.ok) throw new Error(`Tenant list failed (${response.status})`);
      const payload = (await response.json()) as {
        tenants?: AdminTenantOption[];
        defaultTenantId?: string;
      };
      const tenants = Array.isArray(payload.tenants) ? payload.tenants : [];
      setDeployableTenants(tenants);
      setSelectedTenantId((current) => current || payload.defaultTenantId || tenants[0]?.tenantId || '');
    } catch (err) {
      setError((err as Error).message);
    }
  }, [canManageEvidence]);

  const refresh = React.useCallback(async (): Promise<void> => {
    if (!canManageEvidence || !selectedTenantId) return;
    setLoading(true);
    setError(null);
    try {
      const telemetryParams = new URLSearchParams({
        days: String(days),
        tenantId: selectedTenantId
      });
      const [telemetryResp, syntheticResp] = await Promise.all([
        fetch(`/api/admin/evidence/telemetry?${telemetryParams.toString()}`),
        fetch('/api/admin/evidence/synthetics/status')
      ]);
      if (!telemetryResp.ok) throw new Error(`Telemetry request failed (${telemetryResp.status})`);
      if (!syntheticResp.ok) throw new Error(`Synthetic status request failed (${syntheticResp.status})`);
      setTelemetry(await telemetryResp.json());
      setSynthetic(await syntheticResp.json());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [canManageEvidence, days, selectedTenantId]);

  React.useEffect(() => {
    void loadTenants();
  }, [loadTenants]);

  React.useEffect(() => {
    void refresh();
  }, [refresh, selectedTenantId]);

  const runSyntheticNow = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/evidence/synthetics/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId })
      });
      if (!response.ok) {
        throw new Error(`Synthetic run failed (${response.status})`);
      }
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const exportBundle = async (): Promise<void> => {
    if (!auditId.trim()) return;
    setLoading(true);
    setError(null);
    setExportResult(null);
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/admin/evidence/export/audit/${encodeURIComponent(auditId.trim())}?${params.toString()}`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || `Export failed (${response.status})`);
      }
      const payload = await response.json();
      setExportResult({
        manifestSha256: payload.manifestSha256,
        evidenceCount: payload.evidenceCount,
        verificationCount: payload.verificationCount
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (!canManageEvidence) {
    return (
      <div className="card">
        <h1 className="section-title">Evidence Operations</h1>
        <p className="muted">You do not have permission to access evidence operations.</p>
      </div>
    );
  }

  const verificationCounts = telemetry?.verificationsByResult ?? [];
  const countFor = (result: 'match' | 'mismatch' | 'error'): number =>
    verificationCounts.find(row => row.result === result)?.count ?? 0;

  return (
    <div>
      <h1 className="section-title">Evidence Operations</h1>
      <p className="muted">Operational dashboard for telemetry trends, synthetic checks, and immutable verification exports.</p>

      <div className="card" style={{ marginTop: '12px' }}>
        <AdminTenantSelector
          label="Tenant"
          value={selectedTenantId}
          options={deployableTenants}
          onChange={setSelectedTenantId}
          helperText="Evidence operations are tenant-scoped. Review telemetry and run exports for one covered tenant at a time."
        />
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label className="muted" htmlFor="telemetryDays">Telemetry window (days)</label>
          <select
            id="telemetryDays"
            className="input"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            style={{ width: '120px' }}
          >
            <option value={7}>7</option>
            <option value={30}>30</option>
            <option value={90}>90</option>
          </select>
          <button className="btn" type="button" onClick={() => { void refresh(); }} disabled={loading}>
            Refresh
          </button>
          <button className="btn secondary" type="button" onClick={() => { void runSyntheticNow(); }} disabled={loading}>
            Run Synthetic Now
          </button>
        </div>
      </div>

      {error && <div className="pill warning" style={{ marginTop: '12px' }}>{error}</div>}
      {loading && <div className="badge-subtle" style={{ marginTop: '12px' }}>Loading evidence operations…</div>}

      <div className="grid columns-3" style={{ marginTop: '12px' }}>
        <div className="card">
          <h3>Verification Matches</h3>
          <p className="section-title" style={{ margin: 0 }}>{countFor('match')}</p>
        </div>
        <div className="card">
          <h3>Verification Mismatches</h3>
          <p className="section-title" style={{ margin: 0 }}>{countFor('mismatch')}</p>
        </div>
        <div className="card">
          <h3>Verification Errors</h3>
          <p className="section-title" style={{ margin: 0 }}>{countFor('error')}</p>
        </div>
      </div>

      <div className="grid columns-2" style={{ marginTop: '12px' }}>
        <div className="card">
          <h3>Event Counts</h3>
          {(telemetry?.eventsByType ?? []).length === 0 && <p className="muted">No event telemetry in selected window.</p>}
          {(telemetry?.eventsByType ?? []).map((row) => (
            <div key={row.eventType} className="list-row" style={{ cursor: 'default' }}>
              <span>{row.eventType}</span>
              <span className="badge-subtle">{row.count}</span>
            </div>
          ))}
        </div>

        <div className="card">
          <h3>Synthetic Status</h3>
          <p className="muted">Enabled: {synthetic?.enabled ? 'yes' : 'no'} • Interval: {synthetic?.intervalMinutes ?? 0} min</p>
          {(synthetic?.recentRuns ?? []).slice(0, 8).map((run) => (
            <div key={run.runId} className="list-row" style={{ cursor: 'default' }}>
              <div>
                <strong>{run.status}</strong>
                <div className="muted" style={{ fontSize: '0.85rem' }}>{new Date(run.startedAtUtc).toLocaleString()}</div>
              </div>
              <span className="badge-subtle">{run.totalDurationMs} ms</span>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Immutable Verification Export</h3>
        <p className="muted">Generate a deterministic per-audit bundle manifest for compliance handoff.</p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="input"
            placeholder="Audit ID"
            value={auditId}
            onChange={(event) => setAuditId(event.target.value)}
            style={{ flex: '1 1 220px' }}
          />
          <button className="btn" type="button" onClick={() => { void exportBundle(); }} disabled={loading || !auditId.trim()}>
            Generate Bundle
          </button>
        </div>
        {exportResult && (
          <div style={{ marginTop: '10px' }}>
            <div className="badge-subtle">Manifest SHA-256: {exportResult.manifestSha256}</div>
            <div className="badge-subtle">Evidence: {exportResult.evidenceCount} • Verifications: {exportResult.verificationCount}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EvidenceAdmin;
